import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  useTheme,
  useMediaQuery
} from "@material-ui/core";
import { withRouter, Link as RouterLink } from "react-router-dom";
import { makeStyles } from "@material-ui/styles";
import { remCalc } from "../../utils/helpers";
import { API_NO_TOKEN } from "../../utils/axios";
import LoadingBar from "./LoadingBar";
import { useSnackbar } from "notistack";
import FullSreenDialog from "./FullSreenDialog";
import ReCAPTCHA from "react-google-recaptcha";
import { emailRegex } from "../../utils/regExps";

const useStyles = makeStyles(theme => ({
  textField: {
    width: "100%",
    marginTop: theme.spacing(1),
    "&:first-of-type": {
      marginTop: theme.spacing(3)
    },
    // change selection color to primary
    "& input::selection": {
      backgroundColor: theme.palette.primary.main,
      color: "#fff"
    }
  },
  signinWrapper: {
    boxSizing: "border-box",
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(6, 5, 5),
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
    backgroundColor: "#fff",
    boxShadow: "0px 0px 12px rgba(0, 0, 0, 0.08)",
    height: remCalc(486),
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      height: "unset",
      padding: theme.spacing(0),
      boxShadow: "unset"
    }
  },
  signinHeader: {
    marginBottom: theme.spacing(1)
  },
  signinText: {
    lineHeight: "1.75rem",
    fontSize: "1rem"
  },
  link: {
    color: theme.palette.primary.main,
    fontWeight: 500,
    cursor: "pointer",
    lineHeight: "1.5rem",
    "&:hover": {
      textDecoration: "underline"
    }
  },
  button: {
    height: 44,
    textTransform: "none",
    boxShadow: "none !important",
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    fontSize: "0.9375rem"
  },
  buttonSimple: {
    height: 44,
    textTransform: "none",
    minWidth: `${remCalc(48)} !important`,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    fontSize: "0.9375rem"
  },
  labelTextShrinked: {
    transform: "translate(0, 2.5px) scale(0.6875)"
  },
  helperText: {
    margin: theme.spacing(0.5, 0),
    paddingLeft: theme.spacing(1.75),
    fontSize: "0.6875rem"
  },
  helperTextAuth: {
    margin: 0
  }
}));

function ResetPassword({ history }) {
  const classes = useStyles();

  const [inputValues, setInputValues] = useState({
    email: ""
  });

  const theme = useTheme();
  const screenSm = useMediaQuery(theme.breakpoints.down("sm"), {
    noSsr: true
  });

  const [errorValues, setErrorValues] = useState({
    email: ""
  });

  const { enqueueSnackbar } = useSnackbar();

  const recaptcha = useRef();

  const [loading, setLoading] = useState(false);

  const [captchaLoaded, setCaptchaLoaded] = useState(false);

  // 1st step after submit: validate inputs and execute recaptcha
  const handleSubmit = e => {
    e.preventDefault();
    if (emailRegex.test(inputValues.email) && inputValues.email) {
      setErrorValues({ email: "" });
      if (captchaLoaded) {
        recaptcha.current.execute();
      } else {
        enqueueSnackbar(
          "Google reCaptcha not loaded, please try to reload the page"
        );
      }
    } else {
      recaptcha.current.reset();
      setErrorValues({
        ...errorValues,
        email: "Enter a valid email address"
      });
    }
  };

  // 2nd step after submit: after successfull recaptcha send reset request to api
  const onResolved = data => {
    setLoading(true);
    API_NO_TOKEN.get(`/v1/users/password/forgot`, {
      params: { email: inputValues.email, locale: "en" }
    })
      .then(res => {
        setLoading(false);
        console.log(res);
        enqueueSnackbar(
          "Thank you. An email has been sent for you to reset your password.",
          {
            variant: "info",
            anchorOrigin: {
              vertical: "top",
              horizontal: "center"
            },
            autoHideDuration: 8787878,
            className: "info-snackbar",
            classes: {
              root: "info-snackbar-wrapper"
            },
            key: "resetPassword"
          }
        );
        history.replace("/login");
      })
      .catch(err => {
        console.dir(err);
        recaptcha.current.reset();
        if (err && err.response && err.response.data) {
          setErrorValues({ email: err.response.data.localizedMessage });
        }

        setLoading(false);
      });
  };

  const handleChange = e => {
    setInputValues({ ...inputValues, [e.target.name]: e.target.value });
  };

  return (
    <FullSreenDialog>
      <Box
        width={remCalc(408)}
        minHeight={remCalc(436)}
        className={classes.signinWrapper}
        component={"form"}
        display="flex"
        flexDirection="column"
        noValidate
        mb={15}
        onSubmit={handleSubmit}
      >
        {loading && <LoadingBar />}
        {/* Header */}
        <Box>
          <Typography variant="h5" className={classes.signinHeader}>
            Reset password
          </Typography>
          <Typography variant="body1" className={classes.signinText}>
            Enter your email and we will send you instructions to reset your
            password.
          </Typography>
        </Box>

        {/* Inputs */}
        <Box>
          <TextField
            variant="outlined"
            name="email"
            type="email"
            value={inputValues.email}
            onChange={handleChange}
            label="Email"
            className={classes.textField}
            autoFocus={!screenSm}
            helperText={errorValues.email || " "}
            error={!!errorValues.email}
            required
            InputLabelProps={{
              required: false,
              classes: { shrink: classes.labelTextShrinked }
            }}
            FormHelperTextProps={{ classes: { root: classes.helperText } }}
          />
        </Box>

        {/* Actions */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt="auto"
        >
          <Button
            component={RouterLink}
            to="/login"
            color="primary"
            className={classes.buttonSimple}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={classes.button}
            type="submit"
          >
            Submit
          </Button>
        </Box>
      </Box>
      <ReCAPTCHA
        ref={recaptcha}
        sitekey={
          window.Cypress
            ? "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
            : "6Ldd0N8UAAAAAA0WJ0FvzVvi0sk4Ph0NlgStHY-n"
        }
        onChange={(a, b) => {
          console.log(a, b);
          onResolved();
        }}
        asyncScriptOnLoad={e => {
          if (e) {
            setCaptchaLoaded(e.loaded);
          }
        }}
        size="invisible"
        onErrored={(a, b) => console.log("errrrror", a, b)}
        onExpired={(a, b) => console.log("expired", a, b)}
      />
    </FullSreenDialog>
  );
}

export default withRouter(ResetPassword);
